import type { StoredPaymentMethod } from "@/api/model";
import { STORED_PAYMENT_METHOD_ADD_NEW } from "@/modules/payments/constants";
import { formatExpDate, isExpDateExpired } from "./paymentMethodExpiry";

/**
 * Dropdown row for a stored payment method. `id`/`name`/`type` are what the
 * PrimeVue Selects bind to; `expiry`/`isExpired` drive the subtitle and
 * `option-disabled`; `isLastUsed` drives the "Last used" pill and the sort;
 * `data` keeps the raw method for submit paths.
 */
export interface StoredPaymentMethodOption {
  id: string;
  name: string;
  type: string;
  /** "MM/YY", or null when the API has no usable expiry for this method. */
  expiry: string | null;
  isExpired: boolean;
  /** Card most recently charged successfully for this payer (API-derived). */
  isLastUsed: boolean;
  data: StoredPaymentMethod;
}

export const formatStoredPaymentMethodLabel = (
  method: StoredPaymentMethod
): string => {
  const descriptor = String(method.descriptor || "Card");
  const lastFour = String(method.masked_account_number || "").slice(-4);
  return lastFour
    ? `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} •••• ${lastFour}`
    : `${descriptor} - ${method.payment_token}`;
};

export const buildStoredPaymentMethodOption = (
  method: StoredPaymentMethod,
  now: Date = new Date()
): StoredPaymentMethodOption => ({
  id: String(method.payment_token),
  name: formatStoredPaymentMethodLabel(method),
  type: String(method.payment_method_type || "card"),
  expiry: formatExpDate(method.exp_date),
  isExpired: isExpDateExpired(method.exp_date, now),
  isLastUsed: method.is_last_used === true,
  data: method,
});

/**
 * Builds the card rows for a payment picker: the last-used card is pinned to
 * the top (even when expired, so the admin sees why it is disabled) and the
 * rest keep Payabli's order. Every picker goes through this so the sort is
 * defined once.
 */
export const buildStoredPaymentMethodOptions = (
  methods: ReadonlyArray<StoredPaymentMethod>,
  now: Date = new Date()
): StoredPaymentMethodOption[] => {
  const options = methods.map((m) => buildStoredPaymentMethodOption(m, now));
  return [
    ...options.filter((o) => o.isLastUsed),
    ...options.filter((o) => !o.isLastUsed),
  ];
};

/**
 * True when `value` points at an option flagged `isExpired`. PrimeVue's
 * Select honours `option-disabled` for keyboard navigation but not for mouse
 * clicks, so every payment picker checks this before accepting a new value.
 */
export const isExpiredSelection = (
  options: ReadonlyArray<{ id: string; isExpired?: boolean }>,
  value: string | null | undefined
): boolean =>
  value != null && options.some((o) => o.id === value && o.isExpired === true);

/**
 * Pass-through for the payment-method Selects: sets the "Add new method" row
 * apart from the cards with a margin on the `li` (not padding, so the hover
 * highlight stays one row tall). Bind with `:pt-options="{ mergeProps: true }"`.
 */
export const paymentMethodSelectPt = {
  option: ({ context }: { context: { option?: { id?: string } } }) => ({
    class: { "!mt-2": context.option?.id === STORED_PAYMENT_METHOD_ADD_NEW },
  }),
};
